/**
 * Utility functions for detecting and working with preview panels
 */

// Types for preview panel detection
type PreviewPanelIndicators = {
	EMBEDDED_SOURCE: string;
	PREVIEW_PANELS: string;
	CONFLUENCE_PAGE_PREVIEW: string;
};

// Preview panel indicators
const PREVIEW_PANEL_INDICATORS: PreviewPanelIndicators = {
	EMBEDDED_SOURCE: 'embeddedConfluenceSource',
	PREVIEW_PANELS: 'previewPanels',
	CONFLUENCE_PAGE_PREVIEW: 'confluence-page-preview-panel',
};

/**
 * Gets URL search parameters safely
 */
const getUrlSearchParams = (url: string): URLSearchParams | null => {
	try {
		return new URLSearchParams(url);
	} catch {
		return null;
	}
};

/**
 * Checks if a URL contains preview panel indicators
 */
export const hasPreviewPanelIndicators = (searchParams: URLSearchParams): boolean => {
	return (
		searchParams.has(PREVIEW_PANEL_INDICATORS.PREVIEW_PANELS) ||
		searchParams.get(PREVIEW_PANEL_INDICATORS.EMBEDDED_SOURCE) ===
			PREVIEW_PANEL_INDICATORS.CONFLUENCE_PAGE_PREVIEW
	);
};

/**
 * Checks if the current page is being rendered inside a preview panel
 * This function detects various indicators that suggest the page is running in a preview panel context
 *
 * @returns {boolean} True if the page is in a preview panel, false otherwise
 */
export const isInPreviewPanel = (): boolean => {
	try {
		const currentSearch = window.location.search;
		if (currentSearch) {
			const currentParams = getUrlSearchParams(currentSearch);
			if (currentParams && hasPreviewPanelIndicators(currentParams)) {
				return true;
			}
		}
		return false;
	} catch {
		return false;
	}
};

// Helper function to check if we're in an iframe
export const isInIframe = (): boolean => {
	try {
		return window !== window.top;
	} catch {
		// If we can't access window.top due to cross-origin restrictions, assume we're in an iframe
		return true;
	}
};
